import { useEffect, useState } from "react";

export interface TechBreakdownItem {
  layer: string;
  tools: string[];
}

export interface PortfolioFormState {
  title: string;
  category: string;
  description: string;
  thumbnail_url: string;
  video_url: string;
  video_type: string;
  tech_stack: string;
  featured: boolean;
  year: string;
  client_name: string;
  duration: string;
  team_size: string;
  overview: string;
  challenge: string;
  solution: string;
  results: string;
  gallery: string;
  tech_breakdown: TechBreakdownItem[];
  collaborators: string;
  live_url: string;
  user_manual_url: string;
}

type Mode = "create" | "edit";

export default function PortfolioModal({
  open,
  mode,
  initial,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: Mode;
  initial?: (Partial<PortfolioFormState> & { id?: string }) | null;
  onClose: () => void;
  onSubmit: (payload: any) => Promise<void> | void;
}) {
  const title = mode === "create" ? "Add Portfolio Item" : "Edit Portfolio Item";

  const [form, setForm] = useState<PortfolioFormState>({
    title: "",
    category: "",
    description: "",
    thumbnail_url: "",
    video_url: "",
    video_type: "youtube",
    tech_stack: "",
    featured: false,
    year: "",
    client_name: "",
    duration: "",
    team_size: "",
    overview: "",
    challenge: "",
    solution: "",
    results: "",
    gallery: "",
        tech_breakdown: [],
        collaborators: "",
        live_url: "",
        user_manual_url: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newLayer, setNewLayer] = useState("");
  const [newTools, setNewTools] = useState("");

  useEffect(() => {
    if (!open) return;

    setError(null);
    setSubmitting(false);

    if (initial) {
      // Helper to safely convert value to string for inputs
      const toString = (val: any): string => {
        if (val === null || val === undefined) return "";
        if (Array.isArray(val)) return val.join(", ");
        if (typeof val === "string") return val;
        // fallback
        return String(val);
      };

      // Parse collaborators if stored as JSON string or array
      let collaboratorsStr = "";
      if (initial.collaborators) {
        if (typeof initial.collaborators === "string") {
          try {
            const parsed = JSON.parse(initial.collaborators);
            if (Array.isArray(parsed)) {
              collaboratorsStr = parsed.join(", ");
            } else {
              collaboratorsStr = String(initial.collaborators);
            }
          } catch {
            collaboratorsStr = String(initial.collaborators);
          }
        } else if (Array.isArray(initial.collaborators)) {
          collaboratorsStr = (initial.collaborators as string[]).join(", ");
        } else {
          collaboratorsStr = String(initial.collaborators);
        }
      }

      // Parse tech_stack if stored as array or JSON string
      const techStackRaw = initial.tech_stack || [];
      const techStackStr = Array.isArray(techStackRaw)
        ? techStackRaw.join(", ")
        : toString(techStackRaw);

      setForm({
        title: initial.title || "",
        category: initial.category || "",
        description: initial.description || "",
        thumbnail_url: initial.thumbnail_url || "",
        video_url: initial.video_url || "",
        video_type: initial.video_type || "youtube",
        tech_stack: techStackStr,
        featured: initial.featured || false,
        year: initial.year || "",
        client_name: initial.client_name || "",
        duration: initial.duration || "",
        team_size: initial.team_size || "",
        overview: initial.overview || "",
        challenge: initial.challenge || "",
        solution: initial.solution || "",
        results: Array.isArray(initial.results)
          ? (initial.results as any[]).map(r => `${r.metric}: ${r.value} - ${r.description || ''}`).join("\n")
          : "",
        gallery: Array.isArray(initial.gallery)
          ? (initial.gallery as string[]).join("\n")
          : "",
        tech_breakdown: Array.isArray(initial.tech_breakdown)
          ? (initial.tech_breakdown as TechBreakdownItem[])
          : [],
        collaborators: collaboratorsStr,
        live_url: initial.live_url || "",
        user_manual_url: initial.user_manual_url || "",
      });
    } else {
      setForm({
        title: "",
        category: "",
        description: "",
        thumbnail_url: "",
        video_url: "",
        video_type: "youtube",
        tech_stack: "",
        featured: false,
        year: "",
        client_name: "",
        duration: "",
        team_size: "",
        overview: "",
        challenge: "",
        solution: "",
        results: "",
        gallery: "",
        tech_breakdown: [],
        collaborators: "",
        live_url: "",
        user_manual_url: "",
      });
    }
  }, [open, initial]);

  if (!open) return null;

  const parseList = (v: string) => v.split(",").map((x) => x.trim()).filter(Boolean);
  const parseLines = (v: string) => v.split("\n").map((x) => x.trim()).filter(Boolean);

  const addTechLayer = () => {
    if (newLayer.trim() && newTools.trim()) {
      const tools = parseList(newTools);
      setForm((prev) => ({
        ...prev,
        tech_breakdown: [...prev.tech_breakdown, { layer: newLayer.trim(), tools }],
      }));
      setNewLayer("");
      setNewTools("");
    }
  };

  const removeTechLayer = (index: number) => {
    setForm((prev) => ({
      ...prev,
      tech_breakdown: prev.tech_breakdown.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setSubmitting(true);

      const payload: any = {
        // Critical: when editing, ensure we include the original portfolio/project id.
        // Without this, the parent saves via POST (create) instead of PATCH (update).
        ...(initial ? { id: (initial as any).id } : {}),

        title: form.title.trim(),
        category: form.category,
        description: form.description?.trim() || "",
        thumbnail_url: form.thumbnail_url || null,
        video_url: form.video_url || null,
        video_type: form.video_type,
        featured: !!form.featured,
        year: form.year || null,
        client_name: form.client_name || null,
        duration: form.duration || null,
        team_size: form.team_size || null,
        overview: form.overview ?? null,
        challenge: form.challenge ?? null,
        solution: form.solution ?? null,
        results: parseLines(form.results).map(line => {
          const [metricValue, description] = line.split(' - ');
          const [metric, value] = metricValue.split(': ');
          return { metric: metric?.trim() || '', value: value?.trim() || '', description: description?.trim() || '' };
        }).filter(r => r.metric && r.value),
        gallery: parseLines(form.gallery),
        tech_breakdown: form.tech_breakdown,
        live_url: form.live_url ?? null,
        user_manual_url: form.user_manual_url ?? null,
      };

      // Only include tech_stack if provided
      if (form.tech_stack.trim()) {
        payload.tech_stack = parseList(form.tech_stack);
      }

      // Only include collaborators if provided
      if (form.collaborators.trim()) {
        payload.collaborators = JSON.stringify(parseList(form.collaborators));
      }

      await onSubmit(payload);
      setSubmitting(false);
      onClose();
    } catch (err: any) {
      setSubmitting(false);
      setError(err?.message || "Failed to save portfolio item");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="relative bg-dark-panel border border-dark-border rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {title}
            </h3>
            <p className="text-silver text-sm mt-1">All fields are optional. Fill in what you have.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Title</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="Portfolio item title"
              />
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Category</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                placeholder="e.g. Web Development, Mobile App"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Description</span>
            <textarea
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={3}
              placeholder="Brief description of the project"
            />
          </label>

          {/* Media */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Thumbnail URL</span>
              <input
                type="url"
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.thumbnail_url}
                onChange={(e) => setForm((p) => ({ ...p, thumbnail_url: e.target.value }))}
                placeholder="https://example.com/thumbnail.jpg"
              />
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Video URL</span>
              <input
                type="url"
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.video_url}
                onChange={(e) => setForm((p) => ({ ...p, video_url: e.target.value }))}
                placeholder="https://youtube.com/watch?v=..."
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Video Type</span>
              <select
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.video_type}
                onChange={(e) => setForm((p) => ({ ...p, video_type: e.target.value }))}
              >
                <option value="youtube">YouTube</option>
                <option value="vimeo">Vimeo</option>
                <option value="direct">Direct Link</option>
              </select>
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Tech Stack</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.tech_stack}
                onChange={(e) => setForm((p) => ({ ...p, tech_stack: e.target.value }))}
                placeholder="e.g. React, Node.js, PostgreSQL"
              />
            </label>
          </div>

          {/* Project Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Year</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.year}
                onChange={(e) => setForm((p) => ({ ...p, year: e.target.value }))}
                placeholder="2024"
              />
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Client Name</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.client_name}
                onChange={(e) => setForm((p) => ({ ...p, client_name: e.target.value }))}
                placeholder="Client company name"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Duration</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.duration}
                onChange={(e) => setForm((p) => ({ ...p, duration: e.target.value }))}
                placeholder="e.g. 3 months"
              />
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Team Size</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.team_size}
                onChange={(e) => setForm((p) => ({ ...p, team_size: e.target.value }))}
                placeholder="e.g. 5 people"
              />
            </label>
          </div>

          {/* Detailed Content */}
          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Overview</span>
            <textarea
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
              value={form.overview}
              onChange={(e) => setForm((p) => ({ ...p, overview: e.target.value }))}
              rows={3}
              placeholder="High-level overview of the project"
            />
          </label>

          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Challenge</span>
            <textarea
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
              value={form.challenge}
              onChange={(e) => setForm((p) => ({ ...p, challenge: e.target.value }))}
              rows={3}
              placeholder="What challenges were faced?"
            />
          </label>

          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Solution</span>
            <textarea
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
              value={form.solution}
              onChange={(e) => setForm((p) => ({ ...p, solution: e.target.value }))}
              rows={3}
              placeholder="How were the challenges solved?"
            />
          </label>

          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Results</span>
            <textarea
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
              value={form.results}
              onChange={(e) => setForm((p) => ({ ...p, results: e.target.value }))}
              rows={3}
              placeholder="Format: Metric: Value - Description (one per line)&#10;e.g. Performance: 100% - Significantly faster loading"
            />
          </label>

          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Gallery</span>
            <textarea
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
              value={form.gallery}
              onChange={(e) => setForm((p) => ({ ...p, gallery: e.target.value }))}
              rows={3}
              placeholder="Image URLs for gallery (one per line)"
            />
          </label>

          <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Tech Breakdown</span>
          <div className="mt-1 space-y-3">
              {/* Existing layers */}
              {form.tech_breakdown.map((item, index) =>
                <div key={index} className="flex items-center gap-3 p-3 bg-dark-card border border-dark-border rounded-xl">
                  <div className="flex-1">
                    <div className="text-white text-sm font-medium">{item.layer}</div>
                    <div className="text-silver-dim text-xs">{item.tools.join(", ")}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTechLayer(index)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-cyber-red/30 text-cyber-red hover:bg-cyber-red/10 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              )}

              {/* Add new layer */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  className="bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                  value={newLayer}
                  onChange={(e) => setNewLayer(e.target.value)}
                  placeholder="Layer name (e.g. Frontend)"
                />
                <input
                  className="bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                  value={newTools}
                  onChange={(e) => setNewTools(e.target.value)}
                  placeholder="Tools (comma-separated)"
                />
                <button
                  type="button"
                  onClick={addTechLayer}
                  className="bg-electric-blue text-deep-black font-medium px-4 py-2.5 rounded-xl hover:shadow-glow-blue transition-all cursor-pointer text-sm"
                >
                  Add Layer
                </button>
              </div>
          </div>

          <div className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Collaborators</span>
            <input
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
              value={form.collaborators}
              onChange={(e) => setForm((p) => ({ ...p, collaborators: e.target.value }))}
              placeholder="e.g. John Doe, Jane Smith"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Live URL</span>
              <input
                type="url"
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.live_url}
                onChange={(e) => setForm((p) => ({ ...p, live_url: e.target.value }))}
                placeholder="https://example.com"
              />
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">User Manual URL</span>
              <input
                type="url"
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.user_manual_url}
                onChange={(e) => setForm((p) => ({ ...p, user_manual_url: e.target.value }))}
                placeholder="https://example.com/manual.pdf"
              />
            </label>
          </div>

          {/* Featured Toggle - only for create/edit portfolio items, not for projects */}
          {!initial?.id?.startsWith?.("project-") && (
            <div className="flex items-center justify-between p-4 bg-deep-black border border-dark-border rounded-xl">
              <div>
                <p className="text-white text-sm font-medium">Featured Project</p>
                <p className="text-silver-dim text-xs mt-0.5">Show on landing page featured section</p>
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...form, featured: !form.featured })}
                className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 ${form.featured ? "bg-electric-blue" : "bg-dark-border"}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-300 ${form.featured ? "left-5" : "left-0.5"}`} />
              </button>
            </div>
          )}

          {error && <div className="text-cyber-orange text-sm">{error}</div>}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 transition-all text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                submitting ? "bg-electric-blue/40 text-deep-black cursor-not-allowed" : "bg-electric-blue text-deep-black hover:shadow-glow-blue"
              }`}
            >
              {submitting ? "Saving..." : mode === "create" ? "Create" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}